import { getLocale, type Locale } from '@/lib/locale';

export const GROK_IMAGE_EDITOR_PATH = '/image-editor';

export const GROK_IMAGE_EDITOR_SEO_TITLE =
  'Grok Image Editor | Edit Photos with Grok Imagine';

export const GROK_IMAGE_EDITOR_SEO_DESCRIPTION =
  'Use a Grok image editor to upload a photo and describe the change. Remove objects, swap backgrounds, restyle portraits, and download a clean edit.';

export const GROK_IMAGE_EDITOR_DATE_PUBLISHED = '2026-06-30';

export const GROK_IMAGE_EDITOR_LAST_UPDATED = '2026-09-16';

export const GROK_IMAGE_EDITOR_LAST_UPDATED_LABEL = 'September 16, 2026';

export const GROK_IMAGE_EDITOR_KEYWORDS = [
  'grok image editor',
  'grok ai image editor',
  'grok edit image',
  'grok image editor online',
  'grok image to image online',
  'grok photo editor',
].join(', ');

export const GROK_IMAGE_EDITOR_QUICK_ANSWER =
  'A Grok image editor is a prompt-based editor: upload a reference photo, say what should stay and what should change, then generate a new still. This Grok image editor supports up to 5 JPG, PNG, or WebP files under 10MB, private uploads, and no-watermark output. Image-to-image is the same upload-and-prompt flow, not a Photoshop-style layer editor. Safety, copyright, and legal limits still apply.';

export const GROK_IMAGE_EDITOR_WORKBENCH_ANSWER =
  'This Grok AI image editor online lets you upload a private reference image, keep prompt control, generate no-watermark edits, and download the result. The Grok image editor keeps safety and legal limits in place.';

export const GROK_IMAGE_EDITOR_VISUAL_EXAMPLE_GROUPS = [
  {
    title: 'Basic image editing',
    body: 'Use the Grok image editor with a reference image, then change objects, remove unwanted elements, transfer styles, or reshape the surrounding scene without rebuilding the image by hand.',
    layout: 'wide',
    items: [
      {
        label: 'Original image',
        alt: 'Original car image before AI image editing',
        text: 'A source image used as the visual reference for prompt-based editing.',
      },
      {
        label: 'Object change/remover',
        alt: 'Car image after AI object change and object remover editing',
        text: 'The edit changes the main object while keeping the scene readable.',
      },
      {
        label: 'Style transfer',
        alt: 'Car image after AI style transfer editing',
        text: 'The edit applies a new visual style and stronger scene mood.',
      },
    ],
  },
  {
    title: 'Advanced editing features',
    body: 'Use the Grok AI image editor to remove or replace backgrounds, retouch objects, and turn a simple portrait into a cleaner creative direction.',
    layout: 'wide',
    items: [
      {
        label: 'Original image',
        alt: 'Original portrait image before AI background editing',
        text: 'A portrait reference image ready for background and object edits.',
      },
      {
        label: 'Background change',
        alt: 'Portrait after AI background change editing',
        text: 'The edit changes the background while preserving the main person.',
      },
      {
        label: 'Object remover',
        alt: 'Portrait after AI object remover editing',
        text: 'The edit removes selected details for a cleaner result.',
      },
    ],
  },
  {
    title: 'AI-powered enhancements',
    body: 'Use the Grok image editor no restrictions workflow for image text editing, upscale/enhance output, object selection, and cleaner composition.',
    layout: 'poster',
    items: [
      {
        label: 'Original image',
        alt: 'Original poster image before AI enhancement',
        text: 'A poster-style draft used as the input for enhancement and text edits.',
      },
      {
        label: 'Image text editing',
        alt: 'Poster after AI image text editing',
        text: 'The edit changes image text while keeping the poster composition.',
      },
      {
        label: 'Upscale/enhance',
        alt: 'Poster after AI upscale and enhance editing',
        text: 'The edit improves detail and finish for a more polished campaign draft.',
      },
    ],
  },
] as const;

export const GROK_IMAGE_EDITOR_FEATURES = [
  {
    title: 'Reference Image Editing',
    body: 'Upload an image and guide the edit with a prompt. This Grok image editor uses the Grok image to image online workflow for background changes, visual restyling, product mockups, character variations, or concept exploration.',
  },
  {
    title: 'No Unnecessary Prompt Rewriting',
    body: 'Keep more control over your intent. The Grok AI image editor helps your prompt work better without turning your idea into something you did not ask for, which is useful when you need a direct Grok edit image workflow.',
  },
  {
    title: 'Flexible Style Control',
    body: 'Try cinematic, anime, editorial, product, social, realistic, fantasy, or clean commercial styles from the same Grok AI image editor online instead of being locked into a narrow template.',
  },
  {
    title: 'Private Uploads and No Watermark',
    body: 'Use your own reference images in the Grok AI image editor and generate cleaner outputs that are easier to preview, share, crop, or continue editing. Private uploads should not become public gallery assets by default.',
  },
];

export const GROK_IMAGE_EDITOR_STEPS = [
  {
    name: 'Upload a reference image or start from a prompt',
    text: 'Start the Grok image editor by adding a JPG, PNG, or WebP reference image for image-to-image editing, or start from a text prompt when you only need a new visual direction.',
  },
  {
    name: 'Describe the edit clearly',
    text: 'Write what should stay, what should change, and what style you want. Strong prompts mention the subject, background, lighting, composition, and output use case.',
  },
  {
    name: 'Choose model, aspect ratio, resolution, and quality',
    text: 'Select the available model settings that match your goal. Square, portrait, landscape, and vertical formats help prepare the result for different publishing channels.',
  },
  {
    name: 'Generate and download',
    text: 'Generate the edited image, review the Grok AI image editor result, download the clean output, or refine the prompt for another version in the same workspace.',
  },
];

export const GROK_IMAGE_EDITOR_EXAMPLES = [
  {
    title: 'Grok image editor product background edit',
    input:
      'A product photo with a clear main object and a simple original background.',
    prompt:
      'Keep the product shape and label, replace the background with a glossy black studio surface, add soft rim lighting, and make it a clean square ad image.',
    result:
      'A cleaner product image for ad previews, ecommerce tests, or landing page creative.',
  },
  {
    title: 'Grok AI image editor social thumbnail restyle',
    input:
      'A portrait, sketch, or rough creator image that needs a stronger visual identity.',
    prompt:
      'Keep the person recognizable, add a cinematic neon background, improve contrast, and frame the image as a vertical social thumbnail.',
    result:
      'A more polished social post or thumbnail direction with flexible style control.',
  },
  {
    title: 'Grok image editor concept art variation',
    input:
      'A rough concept image, mood board frame, character pose, or scene sketch.',
    prompt:
      'Preserve the character silhouette, make the style more editorial and dramatic, add warm backlight, and simplify the background.',
    result:
      'A fast visual variation for review before committing to a final design direction.',
  },
];

export const GROK_IMAGE_EDITOR_VALUE_ROWS = [
  {
    need: 'Edit without manual layers',
    value:
      'Use the Grok image editor to describe the change you want instead of masking, layering, and retouching every detail by hand.',
  },
  {
    need: 'Keep prompt control',
    value:
      'A Grok image editor no restrictions workflow means no unnecessary prompt rewriting and more control over style, wording, and direction.',
  },
  {
    need: 'Use private references',
    value:
      'Upload your own image as the visual starting point without treating the upload as public gallery content by default.',
  },
  {
    need: 'Create cleaner drafts',
    value:
      'No watermark output from the Grok AI image editor makes images easier to preview, share, crop, download, or continue editing.',
  },
];

export const GROK_IMAGE_EDITOR_USE_CASES = [
  {
    title: 'Product Visuals for Ecommerce and Ads',
    body: 'Use the Grok image editor to upload a product photo, change the background, adjust lighting, and create a cleaner ad-ready image. This is the most common reason people look for a Grok edit image tool.',
  },
  {
    title: 'Social Content and Thumbnails',
    body: 'A Grok AI image editor helps creators turn a portrait, sketch, or rough idea into a polished post image, creator thumbnail, or campaign visual. Flexible styles help match the output to TikTok, Reels, Shorts, X, or feed posts.',
  },
  {
    title: 'Concept Exploration Before Final Design',
    body: 'Use the Grok image editor no restrictions workflow to test several visual directions before committing to a final shoot, campaign style, character design, or product scene. A Grok image to image online workflow makes early creative review faster.',
  },
];

export const GROK_IMAGE_EDITOR_LIMITS = [
  {
    limit: 'Rights-cleared source images',
    meaning:
      'Use images you own, licensed assets, or files you have permission to edit before creating public or commercial outputs.',
    action:
      'Confirm usage rights before uploading brand assets, client files, or private portraits.',
  },
  {
    limit: 'Prompt-based edits, not manual layers',
    meaning:
      'The Grok image editor is best for background changes, style changes, product mockups, thumbnails, and concept exploration.',
    action:
      'Use a traditional layer editor when you need pixel-level manual retouching.',
  },
  {
    limit: 'Object preservation can vary',
    meaning:
      'Exact faces, logos, small text, and fine details can shift depending on model behavior, source quality, and prompt clarity.',
    action:
      'State what must stay unchanged, then review the generated image before publishing.',
  },
  {
    limit: 'Safety and legal limits still apply',
    meaning:
      'Unsafe, illegal, copyrighted, non-consensual, deceptive, or privacy-invasive use is not supported.',
    action:
      'Keep edits lawful, consensual, and rights-cleared even when creative restrictions are fewer.',
  },
  {
    limit: 'Credits are required',
    meaning:
      'Every edit uses credits from a one-time credit pack. The cost depends on the selected model, output quality, and resolution.',
    action:
      'Sign in before generation and check the credit cost shown before each edit.',
  },
];

export const GROK_IMAGE_EDITOR_FAQS = [
  {
    question: 'What is a Grok image editor?',
    answer:
      'A Grok image editor is a prompt-based image editor. You upload a photo, write what should stay and what should change, and generate a new still. It is not a layer, mask, or brush editor.',
  },
  {
    question: 'Is grok image to image the same as this editor?',
    answer:
      'Yes. Grok image to image is this editor’s upload-and-prompt flow: a reference image plus an instruction. You stay on this same Grok image editor page.',
  },
  {
    question: 'How do I remove the bottom bar from an image with Grok Imagine?',
    answer:
      'Upload the image, then describe the bar to remove and what should replace it, such as continuing the background. Review edges and text after the Grok image editor result lands.',
  },
  {
    question: 'How do I use Grok Imagine for background removal?',
    answer:
      'Upload a clear subject photo and ask the Grok image editor to keep the person or product while replacing or simplifying the background. State what must stay unchanged.',
  },
  {
    question: 'How do I remove an object from an image using Grok Imagine?',
    answer:
      'Name the object, say it should disappear, and describe the fill you want. Object preservation can vary, so inspect logos, faces, and small text before you publish.',
  },
  {
    question: 'Is this Grok image editor no restrictions?',
    answer:
      'It means fewer creative workflow restrictions: no watermark, no unnecessary prompt rewriting, flexible styles, and private uploads. Safety rules, copyright rules, and legal limits still apply.',
  },
  {
    question: 'Is this Grok edit image tool free?',
    answer:
      'No. Edits use credits from a one-time credit pack, and there are no free sign-up credits. If you are not logged in, the editor asks you to sign in before generation, and it shows the credit cost before every edit.',
  },
  {
    question: 'Is this an official xAI Grok image editor?',
    answer:
      'This is an independent online Grok-style AI image editing workflow for creators. It is not affiliated with xAI, Grok, X, or the official xAI team unless a partnership is clearly stated.',
  },
  {
    question: 'Are private uploads public?',
    answer:
      'Private uploads should be used only for your editing session and should not become public gallery assets by default. Always avoid uploading private portraits, copyrighted files, or brand assets unless you have permission to edit them.',
  },
];

export const GROK_IMAGE_EDITOR_SCHEMA_FEATURES = [
  'Prompt-based AI image editing from text instructions',
  'Reference image upload for image-to-image workflows',
  'Flexible styles for product, social, portrait, concept, and commercial visuals',
  'Aspect ratio controls for square, portrait, landscape, and vertical outputs',
  'Resolution and quality controls where supported by the selected model',
  'No watermark output and private upload workflow',
];

const EN_GROK_IMAGE_EDITOR_DATA = {
  path: GROK_IMAGE_EDITOR_PATH,
  seoTitle: GROK_IMAGE_EDITOR_SEO_TITLE,
  seoDescription: GROK_IMAGE_EDITOR_SEO_DESCRIPTION,
  datePublished: GROK_IMAGE_EDITOR_DATE_PUBLISHED,
  lastUpdated: GROK_IMAGE_EDITOR_LAST_UPDATED,
  lastUpdatedLabel: GROK_IMAGE_EDITOR_LAST_UPDATED_LABEL,
  keywords: GROK_IMAGE_EDITOR_KEYWORDS,
  quickAnswer: GROK_IMAGE_EDITOR_QUICK_ANSWER,
  workbenchAnswer: GROK_IMAGE_EDITOR_WORKBENCH_ANSWER,
  visualExampleGroups: GROK_IMAGE_EDITOR_VISUAL_EXAMPLE_GROUPS,
  features: GROK_IMAGE_EDITOR_FEATURES,
  steps: GROK_IMAGE_EDITOR_STEPS,
  examples: GROK_IMAGE_EDITOR_EXAMPLES,
  valueRows: GROK_IMAGE_EDITOR_VALUE_ROWS,
  useCases: GROK_IMAGE_EDITOR_USE_CASES,
  limits: GROK_IMAGE_EDITOR_LIMITS,
  faqs: GROK_IMAGE_EDITOR_FAQS,
  schemaFeatures: GROK_IMAGE_EDITOR_SCHEMA_FEATURES,
  copy: {
    hero: {
      eyebrow: 'Quick answer',
      title: 'What is a Grok AI Image Editor?',
      detailsTitle: 'Tool details',
      details: [
        {
          label: 'Primary workflow',
          value: 'Grok image editor no restrictions',
        },
        {
          label: 'Tool type',
          value: 'Grok AI image editor for image-to-image edits',
        },
        {
          label: 'Best input',
          value: 'JPG, PNG, or WebP reference images under 10MB',
        },
        {
          label: 'Last updated',
          value: GROK_IMAGE_EDITOR_LAST_UPDATED_LABEL,
        },
      ],
    },
    examples: {
      eyebrow: 'Editing examples',
      title: 'Image editing examples to try',
      description:
        'Use these Grok image editor examples when you want to brighten a photo, replace a background, remove an object, enhance a draft, or turn a reference image into a cleaner campaign visual.',
    },
    why: {
      eyebrow: 'Why this tool',
      title: 'Why use a Grok image editor no restrictions workflow?',
      description:
        'A practical Grok image editor no restrictions workflow should let you start from an existing image, keep control of your prompt, and test different visual styles without the editor rewriting your prompt unnecessarily.',
      cards: [
        {
          title: 'Fewer creative workflow restrictions',
          body: 'A Grok image editor no restrictions workflow supports online edits with no watermark, no unnecessary prompt rewriting, flexible styles, and private uploads. This gives creators more control while keeping safety and legal limits in place.',
        },
        {
          title: 'Built for image-to-image edits',
          body: 'This Grok AI image editor is strongest when you already have a reference image for product visuals, thumbnails, portraits, concept art, ads, and social posts. Use it when a text-only generator is not enough.',
        },
      ],
    },
    features: {
      eyebrow: 'Function introduction',
      title: 'Core features for the Grok AI image editor online',
      description:
        'The Grok AI image editor keeps reference upload, prompt control, style direction, model settings, and clean output in one workspace so creators can move from idea to result quickly.',
    },
    steps: {
      eyebrow: 'How it works',
      title: 'How to use the Grok edit image workflow',
      description:
        'The best Grok image editor results come from a clear source image and a prompt that separates what should stay from what should change.',
    },
    promptExamples: {
      eyebrow: 'Prompt examples',
      title: 'Prompt examples for Grok image-to-image edits',
      description:
        'Use these Grok image editor prompt ideas when you want to change a background, restyle a portrait, or turn a rough concept into a cleaner visual.',
      headers: {
        useCase: 'Use case',
        input: 'Input image',
        prompt: 'Edit prompt',
        result: 'Expected result',
      },
    },
    useCases: {
      eyebrow: 'Use cases',
      title: 'Where Grok edit image workflows fit best',
      description:
        'Use the Grok image editor when you already have a visual starting point and want fast, prompt-based creative variations before final production.',
    },
    value: {
      eyebrow: 'Value',
      title: 'Why choose this Grok AI image editor online?',
      headers: {
        need: 'Need',
        helps: 'How it helps',
      },
    },
    limits: {
      eyebrow: 'Limits',
      title: 'Best inputs and realistic limits',
      headers: {
        limit: 'Limit',
        meaning: 'What it means',
        action: 'Recommended action',
      },
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Grok image editor no restrictions FAQ',
      description:
        'Common questions about privacy, credits, safe use, and what no restrictions means in real editing work.',
    },
    cta: {
      eyebrow: 'Ready to edit your image?',
      title:
        'Upload a private reference image and generate a clean Grok AI image editor result.',
      edit: 'Edit image online',
      animate: 'Animate edited image',
      history: 'View history',
    },
    schema: {
      softwareName: 'Grok Image Editor',
      actionName: 'Edit an image online',
      resultName: 'AI-edited image',
      howToName: 'How to use the Grok image editor',
      howToDescription:
        'Upload a reference image or start from a prompt, describe the edit, choose model settings, then generate and download the result.',
      breadcrumbHome: 'Home',
      breadcrumbPage: 'Grok Image Editor',
    },
  },
};

type GrokImageEditorSeoData = typeof EN_GROK_IMAGE_EDITOR_DATA;

const ES_GROK_IMAGE_EDITOR_DATA: GrokImageEditorSeoData = {
  ...EN_GROK_IMAGE_EDITOR_DATA,
  seoTitle: 'Grok image editor online | Editor de imagenes Grok',
  seoDescription:
    'Usa Grok image editor online para editar imagenes con prompts, referencias privadas, estilos flexibles y salida sin marca de agua.',
  lastUpdatedLabel: '1 de julio de 2026',
  keywords: [
    'grok image editor',
    'grok editor de imagenes',
    'editor de imagenes grok',
    'grok image editor online',
    'grok image editor alternative',
    'editar imagenes con grok',
  ].join(', '),
  quickAnswer:
    'Este Grok image editor es un editor de imagenes online para creadores que prefieren editar con prompts en vez de herramientas manuales complejas. Permite subir imagenes de referencia JPG, PNG o WebP, controlar estilos, mantener subidas privadas y generar salidas sin marca de agua. Menos restricciones significa mas flexibilidad de flujo creativo, no uso ilimitado o inseguro.',
  workbenchAnswer:
    'Este editor Grok AI permite subir una imagen privada de referencia, mantener control del prompt, generar ediciones sin marca de agua y descargar el resultado. Los limites legales y de seguridad siguen aplicando.',
  visualExampleGroups: EN_GROK_IMAGE_EDITOR_DATA.visualExampleGroups.map(
    (group) => ({
      ...group,
      title:
        group.title === 'Basic image editing'
          ? 'Edicion basica de imagen'
          : group.title === 'Advanced editing features'
            ? 'Funciones avanzadas de edicion'
            : 'Mejoras con IA',
      body:
        group.title === 'Basic image editing'
          ? 'Usa el Grok image editor con una referencia para cambiar objetos, eliminar elementos, transferir estilos o redisenar la escena sin rehacer la imagen a mano.'
          : group.title === 'Advanced editing features'
            ? 'Usa el editor para cambiar fondos, retocar objetos y convertir un retrato simple en una direccion creativa mas limpia.'
            : 'Usa el flujo para editar texto en imagen, mejorar salida, seleccionar objetos y limpiar composicion.',
      items: group.items.map((item) => ({
        ...item,
        label:
          item.label === 'Original image'
            ? 'Imagen original'
            : item.label === 'Object change/remover'
              ? 'Cambio/eliminacion de objeto'
              : item.label === 'Style transfer'
                ? 'Transferencia de estilo'
                : item.label === 'Background change'
                  ? 'Cambio de fondo'
                  : item.label === 'Object remover'
                    ? 'Eliminador de objetos'
                    : item.label === 'Image text editing'
                      ? 'Edicion de texto en imagen'
                      : item.label === 'Upscale/enhance'
                        ? 'Mejora/upscale'
                        : item.label,
      })),
    })
  ),
  features: [
    {
      title: 'Edicion con imagen de referencia',
      body: 'Sube una imagen y guia la edicion con un prompt. El flujo sirve para cambios de fondo, restyling visual, mockups de producto, variaciones de personaje o exploracion de conceptos.',
    },
    {
      title: 'Menos reescritura innecesaria del prompt',
      body: 'Mantén mas control sobre tu intencion. El editor ayuda a que el prompt funcione sin convertir la idea en algo que no pediste.',
    },
    {
      title: 'Control flexible de estilo',
      body: 'Prueba estilos cinematicos, anime, editorial, producto, social, realista, fantasy o comercial limpio desde el mismo editor.',
    },
    {
      title: 'Subidas privadas y sin marca de agua',
      body: 'Usa tus propias referencias y genera salidas mas limpias para previsualizar, compartir, recortar o seguir editando.',
    },
  ],
  steps: [
    {
      name: 'Sube una imagen de referencia o empieza con prompt',
      text: 'Anade una imagen JPG, PNG o WebP para edicion image-to-image, o empieza desde texto si solo necesitas una nueva direccion visual.',
    },
    {
      name: 'Describe la edicion con claridad',
      text: 'Escribe que debe mantenerse, que debe cambiar y que estilo quieres. Los prompts fuertes mencionan sujeto, fondo, luz, composicion y uso final.',
    },
    {
      name: 'Elige modelo, aspecto, resolucion y calidad',
      text: 'Selecciona ajustes que encajen con tu objetivo. Formatos cuadrado, retrato, paisaje y vertical preparan el resultado para distintos canales.',
    },
  ],
  promptExamples: [
    {
      useCase: 'Cambiar fondo de producto',
      input: 'Foto de producto limpia sobre fondo neutro.',
      prompt:
        'Mantén el producto igual, cambia el fondo a un estudio minimalista con luz suave y sombra natural.',
      result: 'Visual de producto mas pulido para anuncios o landing pages.',
    },
    {
      useCase: 'Restyling de retrato',
      input: 'Retrato con cara clara y fondo simple.',
      prompt:
        'Convierte el retrato en estilo editorial cinematografico, luz suave, fondo limpio, identidad reconocible.',
      result: 'Retrato creativo para perfil, social o campana.',
    },
    {
      useCase: 'Limpiar composicion',
      input: 'Imagen con objetos que distraen.',
      prompt:
        'Elimina los objetos del fondo, conserva el sujeto principal y mejora la luz general.',
      result: 'Imagen mas clara para publicacion o presentacion.',
    },
  ],
  useCases: [
    {
      title: 'Editar imagenes de producto sin rehacer el asset',
      body: 'Cambia fondo, luz, composicion o estilo mientras mantienes el producto reconocible.',
    },
    {
      title: 'Crear variaciones de personaje o retrato',
      body: 'Usa referencias privadas y prompts para explorar estilos sin perder la identidad principal.',
    },
    {
      title: 'Preparar visuales sociales y de campana',
      body: 'Convierte concepts en versiones mas limpias antes de animarlas o publicarlas.',
    },
  ],
  valueRows: [
    {
      need: 'Control del prompt',
      value:
        'Mantiene la intencion sin reescribir el prompt de forma agresiva.',
    },
    {
      need: 'Referencia visual',
      value: 'Empieza desde una imagen real en vez de texto vacio.',
    },
    {
      need: 'Salida limpia',
      value:
        'Genera resultados sin marca de agua para revisar y continuar editando.',
    },
    {
      need: 'Privacidad practica',
      value:
        'Las subidas privadas no deben convertirse en assets publicos por defecto.',
    },
  ],
  faqs: [
    {
      question: 'Que es Grok image editor?',
      answer:
        'Es un flujo de edicion de imagenes con IA que usa una referencia y un prompt para cambiar fondo, estilo, objetos o composicion.',
    },
    {
      question: 'Grok editor de imagenes es gratis?',
      answer:
        'No. Cada edicion usa creditos de un paquete de pago unico y no hay creditos gratis al registrarse. El coste se muestra antes de cada edicion.',
    },
    {
      question: 'Que significa menos restricciones?',
      answer:
        'Significa menos friccion creativa como sin marca de agua, subidas privadas y control flexible de estilo. No significa uso ilimitado, inseguro o sin reglas.',
    },
    {
      question: 'Puedo subir imagenes privadas?',
      answer:
        'Solo sube imagenes que tengas permiso para usar. Evita retratos privados, marcas o material protegido sin autorizacion.',
    },
    {
      question: 'Este sitio es oficial de xAI?',
      answer:
        'No. Crazy Frog AI es independiente y no esta afiliado, respaldado ni patrocinado por xAI, Grok o X.',
    },
  ],
  schemaFeatures: [
    'Editor de imagenes Grok con imagen de referencia',
    'Edicion mediante prompts para fondo, objeto, estilo y composicion',
    'Subidas privadas y salida sin marca de agua',
    'Control de aspecto, modelo, resolucion y calidad',
    'FAQ visible sobre seguridad, privacidad y uso realista',
  ],
  copy: {
    ...EN_GROK_IMAGE_EDITOR_DATA.copy,
    hero: {
      eyebrow: 'Editor de imagenes Grok',
      title:
        'Edita imagenes con prompts, referencias privadas y estilos flexibles.',
      description:
        'Sube una imagen, describe el cambio y genera una edicion limpia sin perder control creativo.',
      primaryCta: 'Editar imagen',
      secondaryCta: 'Animar resultado',
      details: [
        {
          label: 'Tipo de tool',
          value: 'Grok AI image editor para edicion image-to-image',
        },
        {
          label: 'Mejor input',
          value: 'JPG, PNG o WebP bajo 10MB',
        },
        {
          label: 'Ultima actualizacion',
          value: '1 de julio de 2026',
        },
      ],
    },
    examples: {
      eyebrow: 'Ejemplos de edicion',
      title: 'Ejemplos de imagen que puedes probar',
      description:
        'Usa estos ejemplos para aclarar una foto, cambiar fondo, quitar objetos o convertir una referencia en visual de campana.',
      attributionPrefix: 'Los ejemplos visuales toman como referencia',
      attributionSuffix: 'ejemplos publicos.',
    },
    why: {
      eyebrow: 'Por que usarlo',
      title: 'Por que elegir un flujo Grok image editor mas flexible?',
      description:
        'Un buen editor empieza desde una imagen existente, mantiene control del prompt y permite probar estilos sin que el sistema cambie la intencion.',
      cards: [
        {
          title: 'Menos friccion de flujo creativo',
          body: 'Sin marca de agua, menos reescritura de prompt, estilos flexibles y subidas privadas dan mas control manteniendo limites legales y de seguridad.',
        },
        {
          title: 'Optimizado para image-to-image',
          body: 'Es fuerte cuando ya tienes producto, retrato, concept art, anuncio o post social como punto de partida.',
        },
      ],
    },
    features: {
      eyebrow: 'Funciones',
      title: 'Funciones clave del Grok AI image editor online',
      description:
        'Referencia, prompt, estilo, modelo y salida limpia viven en el mismo workspace.',
    },
    steps: {
      eyebrow: 'Como funciona',
      title: 'Como usar el Grok image editor',
      description:
        'Un buen resultado nace de una fuente clara y un prompt que separa que conservar y que cambiar.',
    },
    promptExamples: {
      eyebrow: 'Prompts',
      title: 'Ejemplos de prompts para editar imagenes con Grok',
      description:
        'Usa estos prompts para cambiar fondos, estilo o limpiar conceptos.',
      headers: {
        useCase: 'Caso',
        input: 'Imagen',
        prompt: 'Prompt de edicion',
        result: 'Resultado esperado',
      },
    },
    useCases: {
      eyebrow: 'Casos de uso',
      title: 'Cuando encaja el editor de imagenes Grok',
      description:
        'Cuando ya tienes una base visual y quieres variaciones rapidas antes de producir o publicar.',
    },
    value: {
      eyebrow: 'Valor',
      title: 'Por que elegir este Grok image editor online?',
      headers: {
        need: 'Necesidad',
        helps: 'Como ayuda',
      },
    },
    limits: {
      eyebrow: 'Limites',
      title: 'Buenos inputs y limites realistas',
      headers: {
        limit: 'Limite',
        meaning: 'Que significa',
        action: 'Accion recomendada',
      },
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'FAQ de Grok image editor',
      description:
        'Preguntas sobre privacidad, creditos, seguridad y que significa tener menos restricciones.',
    },
    cta: {
      eyebrow: 'Listo para editar?',
      title:
        'Sube una referencia privada y genera una edicion limpia con Grok AI.',
      edit: 'Editar imagen online',
      animate: 'Animar imagen editada',
      history: 'Ver historial',
    },
    schema: {
      softwareName: 'Grok image editor online',
      actionName: 'Editar imagen online',
      resultName: 'Imagen editada con IA',
      howToName: 'Como usar Grok image editor online',
      howToDescription:
        'Sube una imagen de referencia, describe la edicion, elige ajustes y genera el resultado.',
      breadcrumbHome: 'Inicio',
      breadcrumbPage: 'Grok image editor online',
    },
  },
};

export function getGrokImageEditorSeoData(
  locale: Locale = getLocale()
): GrokImageEditorSeoData {
  if (locale === 'es') return ES_GROK_IMAGE_EDITOR_DATA;
  return EN_GROK_IMAGE_EDITOR_DATA;
}
